// Ниши › Откриване — the filter half (Google types, name keywords) and the vocabulary half
// (synonyms, services, languages, phrasings) that the query matrix is built from.

import type { SetVerticalField, VerticalPatch } from './useVerticals.js';
import { LinesField, NumberField, ToggleField } from './fields.js';

const LANGS: readonly { key: string; label: string }[] = [
  { key: 'bg', label: 'кирилица (бг)' },
  { key: 'lat', label: 'латиница (zabolekar)' },
  { key: 'en', label: 'английски (туристически ниши)' },
];

export function VerticalDiscoveryTab({
  draft,
  set,
}: {
  draft: VerticalPatch;
  set: SetVerticalField;
}) {
  const toggleLang = (key: string, on: boolean) => {
    const next = draft.search_languages.filter((l) => l !== key);
    set('search_languages', on ? [...next, key] : next);
  };

  return (
    <div className="adm__form adm__form--cols">
      <fieldset className="adm__fieldset">
        <legend className="adm__legend">Филтър на резултатите</legend>
        <LinesField
          label="Google типове (places_included_types)"
          value={draft.places_included_types}
          onChange={(v) => set('places_included_types', v)}
          hint="Непознат тип кара Google да отговори с грешка 400 — ползвайте само типове от списъка на Places."
        />
        <LinesField
          label="Думи в името — включва"
          value={draft.name_include_kw}
          onChange={(v) => set('name_include_kw', v)}
          hint="Поне една трябва да се среща в името, ако типът не е сигурен."
        />
        <LinesField
          label="Думи в името — изключва"
          value={draft.name_exclude_kw}
          onChange={(v) => set('name_exclude_kw', v)}
        />
        <LinesField
          label="OSM тагове (key=value)"
          value={draft.osm_tags}
          onChange={(v) => set('osm_tags', v)}
          hint="amenity=dentist, shop=beauty — с какво се търси в OpenStreetMap."
        />
        <LinesField
          label="Системи за записване (booking_hints)"
          value={draft.booking_hints}
          onChange={(v) => set('booking_hints', v)}
          hint="superdoc, booksy — домейни, които одитът брои за онлайн записване."
          rows={3}
        />
      </fieldset>

      <fieldset className="adm__fieldset">
        <legend className="adm__legend">Речник за заявките</legend>
        <LinesField
          label="Синоними (кирилица)"
          value={draft.synonyms_bg}
          onChange={(v) => set('synonyms_bg', v)}
          hint="Първият е основният — само той влиза в заявките по намерение."
        />
        <LinesField
          label="Синоними (латиница)"
          value={draft.synonyms_lat}
          onChange={(v) => set('synonyms_lat', v)}
          hint="Както хората наистина пишат: zabolekar, manikur — не машинна транслитерация."
        />
        <LinesField
          label="Услуги (кирилица)"
          value={draft.services_bg}
          onChange={(v) => set('services_bg', v)}
        />
        <LinesField
          label="Услуги (латиница)"
          value={draft.services_lat}
          onChange={(v) => set('services_lat', v)}
        />
        <LinesField
          label="Фрази по намерение"
          value={draft.intent_phrasings_bg}
          onChange={(v) => set('intent_phrasings_bg', v)}
          hint="{syn} и {city} се заместват: „{syn} {city} цени“."
          rows={3}
        />
        <div className="adm__field">
          <span className="adm__field-label">Езици на търсене</span>
          <div className="adm__checks">
            {LANGS.map((lang) => (
              <label key={lang.key} className="adm__check">
                <input
                  type="checkbox"
                  className="adm__toggle-check"
                  checked={draft.search_languages.includes(lang.key)}
                  onChange={(event) => toggleLang(lang.key, event.target.checked)}
                />
                <span>{lang.label}</span>
              </label>
            ))}
          </div>
        </div>
        <ToggleField
          label="Мрежово претърсване (grid sweep)"
          checked={draft.grid_sweep}
          onChange={(v) => set('grid_sweep', v)}
          hint="Шестоъгълната мрежа по типове, независимо от заявките."
        />
        <NumberField
          label="Дял от дневния лимит (discovery_share)"
          value={draft.discovery_share}
          onChange={(v) => set('discovery_share', v ?? 1)}
          min={1}
          max={10}
          hint="Относителен дял при няколко активни ниши: 2 срещу 1 означава две трети от заявките."
        />
      </fieldset>
    </div>
  );
}
